
import type { AuthSession } from '@aws-amplify/auth'
import { fetchAuthSession } from '@aws-amplify/auth'

import { AppError } from '../libraries/errors/AppError'
import HttpStatusCode from './errors/HttpStatusCode'
import { ANALYTICS_EVENTS, getGlobalTrackEvent } from '../libraries/posthog'

export interface FetchOptions extends RequestInit {
	skipContentType?: boolean;
}

export const getIdToken = async (): Promise<string | undefined> => {
	try {
		// get tokenId from local storage
		const session: AuthSession = await fetchAuthSession()
		if(!session?.tokens?.idToken) {
			return undefined
		}
		const { tokens: { idToken: { payload: { aud, sub } } } } = session
		return window.localStorage.getItem(`CognitoIdentityServiceProvider.${aud}.${sub}.idToken`)?.toString()
	} catch (err) {
		throw new AppError('Error getting id token')
	}
}

// Global flag to track if we're redirecting
let isRedirecting = false

const authFetcher = async (url: string | URL, options: FetchOptions): Promise<Response | null> => {
	try {
		const idToken = await getIdToken()

		const headers: HeadersInit = {
			Authorization: `Bearer ${idToken}`
		}

		// Only add Content-Type for JSON requests, not for FormData
		if (!options.skipContentType) {
			headers['Content-Type'] = 'application/json'
		}

		const res = await fetch(url, {
			...options,
			credentials: 'include',
			headers: {
				...headers,
				...options.headers
			}
		})

		if (res.status === 204) return null // 204 does not have body

		if(res.status === 404) {
			throw new AppError('Resource not found', res.status)
		}

		if(res.status !== HttpStatusCode.OK) {
			const payload = await res.json()
			throw new AppError(payload.error, res.status)
		}

		return res
	} catch(error: unknown) {
		// Check if the request was aborted due to navigation
		if (error instanceof DOMException && error.name === 'AbortError') {
			// Don't throw aborted requests as errors
			return null
		}

		// Check if it's a fetch error and we're already redirecting
		if (isRedirecting && (error instanceof TypeError || (error as Error).name === 'TypeError')) {
			// Silently ignore fetch errors during redirect
			return null
		}

		if(error instanceof AppError && error.code === HttpStatusCode.PAYMENT_REQUIRED) {
			const trackEvent = getGlobalTrackEvent()
			if (trackEvent) {
				trackEvent(ANALYTICS_EVENTS.REACHED_FREE_USAGES_LIMIT)
			}
			window.location.replace('/upgrade-pro')
			isRedirecting = true
			return null
		}
		throw error
	}
}

/**
 * Authorised fetch to our api.
 */
const fetcher = async (url: string, options: FetchOptions = {}) => {
	// Create AbortController if one doesn't exist
	if (!options.signal) {
		const controller = new AbortController()
		options.signal = controller.signal
	}

	const res = await authFetcher(url, options)

	if(!res) { return res }

	const payload = await res.json()

	if (res.ok) {
		console.debug('-----')
		console.debug(`${(options as { method: string }).method || 'GET'} ${url}`)
		console.debug(payload)
		console.debug('-----')
		return payload
	} else {
		throw new AppError('Something went wrong')
	}
}

/**
 * Authorised form data fetch to our api.
 * Use this for file uploads and form submissions with files.
 */
export const formDataFetcher = async (url: string, formData: FormData, method: string = 'POST') => {
	const options: FetchOptions = {
		method,
		body: formData,
		skipContentType: true // Skip automatic Content-Type for FormData
	}

	const res = await authFetcher(url, options)
	if (!res) { return res }

	const payload = await res.json()

	if (res.ok) {
		console.debug('-----')
		console.debug(`${method} ${url} (FormData)`)
		console.debug(payload)
		console.debug('-----')
		return payload
	} else {
		throw new AppError('Something went wrong')
	}
}

/**
 * Authorised stream text fetch to our api
 */
export const streamTextFetcher = async (url: string, init?: RequestInit): Promise<Response> => {
	const response = await fetch(url, {
		...init,
		headers: {
			...init?.headers,
			Authorization: `Bearer ${await getIdToken()}`
		}
	})

	if (!response.ok) {
		throw new AppError(`HTTP error! status: ${response.status}`)
	}

	return response
}

export default fetcher
